import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../api/client';
import { getAuthToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function AssignmentsList({ classId, isTeacher = false, onSelectAssignment, refreshTrigger = 0 }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Teacher: create assignment form
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (classId) {
      loadAssignments();
    }
  }, [classId]);

  // Reload when refreshTrigger changes (e.g., new attempt submitted)
  useEffect(() => {
    if (classId && refreshTrigger > 0) {
      console.log('[Assignments] Refreshing due to new attempt, trigger:', refreshTrigger);
      loadAssignments();
    }
  }, [refreshTrigger]);

  // Auto-poll every 5 seconds for teachers to catch new attempts
  useEffect(() => {
    if (!classId || !isTeacher) return;

    const pollInterval = setInterval(() => {
      loadAssignments(false); // Don't show loading spinner during polling
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [classId, isTeacher]);

  const loadAssignments = async (showLoading = true) => {
    try {
      if (showLoading && assignments.length === 0) {
        setLoading(true);
      }
      const data = await apiClient.get(`/classes/${classId}/assignments`);
      setAssignments(data.assignments || []);
    } catch (err) {
      setError('Ülesannete laadimine ebaõnnestus');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!imageFile || !title.trim()) {
      setError('Pealkiri ja pilt on kohustuslikud');
      return;
    }

    setCreating(true);
    setError('');

    try {
      // Convert image to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result;
          if (typeof result === 'string') {
            // Remove data URL prefix (e.g., "data:image/png;base64,")
            const base64Data = result.split(',')[1];
            resolve(base64Data);
          } else {
            reject(new Error('Failed to read image'));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read image file'));
        reader.readAsDataURL(imageFile);
      });

      console.log('Sending assignment, image size:', base64.length, 'chars');

      await apiClient.post(`/classes/${classId}/assignments`, {
        title: title.trim(),
        description: description.trim() || null,
        task_image: base64
      });

      console.log('Assignment created successfully');

      // Reset form
      setTitle('');
      setDescription('');
      setImageFile(null);
      setImagePreview(null);
      setShowForm(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Reload assignments
      loadAssignments();
    } catch (err) {
      console.error('Assignment creation failed:', err);
      setError(err.message || 'Ülesande loomine ebaõnnestus');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (assignment) => {
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_URL}/classes/${classId}/assignments/${assignment.id}?is_active=${!assignment.isActive}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (!response.ok) throw new Error('Update failed');
      loadAssignments();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (assignment) => {
    if (!window.confirm(`Kas oled kindel, et soovid kustutada "${assignment.title}"?`)) {
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_URL}/classes/${classId}/assignments/${assignment.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (!response.ok) throw new Error('Delete failed');
      loadAssignments();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
        Laadin ülesandeid...
      </div>
    );
  }

  return (
    <div>
      {/* Teacher: Create Assignment Button & Form */}
      {isTeacher && (
        <div style={{ marginBottom: '24px' }}>
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                background: '#8b5cf6',
                color: 'white',
                border: 'none',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              + Lisa ülesanne
            </button>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
              border: '2px solid #8b5cf6'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700' }}>
                Uus ülesanne
              </h3>

              <form onSubmit={handleCreateAssignment}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600' }}>
                    Pealkiri *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="nt. Ülesanne 5, lk 23"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600' }}>
                    Kirjeldus (valikuline)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Lisainfo ülesande kohta..."
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600' }}>
                    Ülesande pilt *
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px dashed #e5e7eb',
                      fontSize: '14px'
                    }}
                  />
                  {imagePreview && (
                    <div style={{ marginTop: '12px' }}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          maxWidth: '200px',
                          maxHeight: '150px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}
                      />
                    </div>
                  )}
                </div>

                {error && (
                  <div style={{
                    padding: '12px',
                    borderRadius: '8px',
                    background: '#fef2f2',
                    color: '#b91c1c',
                    fontSize: '13px',
                    marginBottom: '16px'
                  }}>
                    {error}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit"
                    disabled={creating}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      background: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: creating ? 'not-allowed' : 'pointer',
                      opacity: creating ? 0.6 : 1
                    }}
                  >
                    {creating ? 'Loon...' : 'Loo ülesanne'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setTitle('');
                      setDescription('');
                      setImageFile(null);
                      setImagePreview(null);
                      setError('');
                    }}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Tühista
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Assignments List */}
      {assignments.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '48px',
          color: '#6b7280',
          fontSize: '16px',
          background: 'white',
          borderRadius: '12px'
        }}>
          {isTeacher
            ? 'Pole veel ülesandeid. Lisa esimene ülesanne!'
            : 'Ülesandeid pole veel lisatud'}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '16px'
        }}>
          {assignments.map((assignment) => (
            <div
              key={assignment.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                border: assignment.isActive === false ? '2px dashed #e5e7eb' : 'none',
                opacity: assignment.isActive === false ? 0.6 : 1
              }}
            >
              {/* Thumbnail */}
              {assignment.thumbnail && (
                <div style={{
                  height: '120px',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  <img
                    src={`data:image/jpeg;base64,${assignment.thumbnail}`}
                    alt={assignment.title}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              )}

              <div style={{ padding: '16px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  margin: '0 0 8px 0',
                  color: '#111827'
                }}>
                  {assignment.title}
                </h3>

                {assignment.description && (
                  <p style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    margin: '0 0 12px 0'
                  }}>
                    {assignment.description}
                  </p>
                )}

                {/* Stats */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  fontSize: '13px',
                  color: '#6b7280',
                  marginBottom: '12px'
                }}>
                  <span>{assignment.attemptCount || 0} katset</span>
                  <span style={{ color: '#22c55e' }}>{assignment.correctCount || 0} õiget</span>
                  <span>{assignment.passRate || 0}%</span>
                </div>

                {/* Error Steps (teachers only) */}
                {isTeacher && assignment.errorSteps && assignment.errorSteps.length > 0 && (
                  <div style={{
                    padding: '8px 12px',
                    background: '#fef2f2',
                    borderRadius: '6px',
                    marginBottom: '12px',
                    fontSize: '12px'
                  }}>
                    <div style={{ fontWeight: '600', color: '#b91c1c', marginBottom: '4px' }}>
                      Kus tehakse vigu:
                    </div>
                    {assignment.errorSteps.map((step, i) => (
                      <div key={i} style={{ color: '#7f1d1d' }}>
                        {step.step}: {step.count}x
                      </div>
                    ))}
                  </div>
                )}

                {/* Student: Show their status */}
                {!isTeacher && assignment.attempted !== null && (
                  <div style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    background: assignment.wasCorrect ? '#ecfdf3' : '#fef2f2',
                    color: assignment.wasCorrect ? '#166534' : '#b91c1c',
                    fontSize: '13px',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    {assignment.attempted
                      ? (assignment.wasCorrect ? '✓ Lahendatud' : '✗ Proovi uuesti')
                      : 'Lahendamata'}
                  </div>
                )}

                {/* Actions */}
                {isTeacher ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleToggleActive(assignment)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        background: assignment.isActive ? '#fef2f2' : '#ecfdf3',
                        color: assignment.isActive ? '#b91c1c' : '#166534',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {assignment.isActive ? 'Peida' : 'Aktiveeri'}
                    </button>
                    <button
                      onClick={() => handleDelete(assignment)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #fecaca',
                        background: 'white',
                        color: '#b91c1c',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Kustuta
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => onSelectAssignment && onSelectAssignment(assignment)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      background: '#2563ff',
                      color: 'white',
                      border: 'none',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Lahenda
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AssignmentsList;
